// src/components/dashboard/DashboardDetectionFindings.tsx
//
// "Detection" — Activity nav group. Surfaces pending detection findings
// across every live outcome, plus each run's overall result, per the
// user story's five scenarios (nothing pending / something pending /
// full run picture / volume / resolve-and-refresh).
//
// Backend: GET /dashboard/api/detection/findings — see dashboard.ts.
//
// UPDATED — resolution is now available inline, via
// POST /dashboard/api/detection/findings/resolve (dashboard.ts's
// resolveFinding route, added after this file's original header
// comment was written — that comment's claim that "resolution itself
// happens through chat" is now stale and has been removed). Reuses the
// exact enforced-genuine-note pattern DashboardThreadDetail.tsx's
// RecordFindingForm already established: minimum-length resolutionNote,
// explicit copy about why a genuine account matters, open/close toggle.
// "Resolve via chat" link kept as a secondary option, not the only path.

import React, { useState, useEffect } from 'react';
import { authedFetch } from '../../services/dashboardApi';
import { Loader2Icon, AlertTriangleIcon, CheckCircle2Icon, MessageSquareIcon, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface RunSummary {
  outcomeId: string;
  outcomeName: string;
  runId: string;
  status: 'in_progress' | 'complete';
  totalFindings: number;
  resolvedFindings: number;
  startedAt: string;
  completedAt: string | null;
}

interface PendingFinding {
  outcomeId: string;
  outcomeName: string;
  runId: string;
  transactionId: string;
  discrepancyType: string | null;
  severity: 'none' | 'minor' | 'material';
  recommendation: 'auto_complete' | 'escalate';
  reason: string;
  createdAt: string;
}

interface FindingsResponse {
  runs: RunSummary[];
  pending: PendingFinding[];
  totalPending: number;
  truncated: boolean;
}

function severityBadge(severity: PendingFinding['severity']) {
  const styles: Record<string, string> = {
    material: 'bg-red-50 text-red-700',
    minor: 'bg-amber-50 text-amber-700',
    none: 'bg-brand-surface text-brand-gray',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-mono ${styles[severity]}`}>
      {severity}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Run summary strip — Scenario 3: the whole run's result, not just
// the isolated backlog, so a clean run and a run with a real problem
// don't look identical.
function RunSummaryRow({ run }: { run: RunSummary }) {
  const isClean = run.status === 'complete' && run.totalFindings === run.resolvedFindings;
  return (
    <Link
      to={`/dashboard/detection/runs/${run.runId}/trace?outcomeId=${run.outcomeId}`}
      className="flex items-center justify-between rounded-lg border border-brand-border bg-brand-white px-3 py-2 text-sm hover:bg-brand-surface transition-colors"
    >
      <div className="flex items-center gap-2">
        {isClean ? (
          <CheckCircle2Icon className="size-4 text-green-600" />
        ) : run.status === 'in_progress' ? (
          <AlertTriangleIcon className="size-4 text-amber-600" />
        ) : (
          <CheckCircle2Icon className="size-4 text-green-600" />
        )}
        <span className="font-medium text-brand-black">{run.outcomeName}</span>
        <span className="text-brand-gray font-mono text-xs">
          {formatDate(run.startedAt)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-brand-gray">
          {run.resolvedFindings}/{run.totalFindings} resolved
          {run.status === 'in_progress' && run.totalFindings > run.resolvedFindings ? ' — needs review' : ''}
        </span>
        <ChevronRight className="size-3.5 text-brand-gray" />
      </div>
    </Link>
  );
}

// ── Inline resolve form — same enforced-genuine-note pattern as
// DashboardThreadDetail.tsx's RecordFindingForm.
function ResolveFindingInline({
  finding,
  onResolved,
}: {
  finding: PendingFinding;
  onResolved: () => void;
}) {
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    if (resolutionNote.trim().length < 1) {
      setError('Enter how this was resolved — a genuine account, not a placeholder.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await authedFetch('/detection/findings/resolve', {
        method: 'POST',
        body: JSON.stringify({
          outcomeId: finding.outcomeId,
          runId: finding.runId,
          transactionId: finding.transactionId,
          resolutionNote: resolutionNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resolve finding.');
      setResolutionNote('');
      setOpen(false);
      onResolved();
    } catch (err: any) {
      setError(err.message || 'Could not resolve this finding.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-3 rounded-lg border border-brand-border px-3 py-1.5 text-xs font-medium text-brand-charcoal hover:bg-brand-surface"
      >
        Resolve
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-brand-border bg-brand-surface p-3">
      <p className="text-xs text-brand-gray">
        e.g. "confirmed with vendor, approved" or "miscategorized, corrected to Office Supplies."
      </p>
      <textarea
        value={resolutionNote}
        onChange={(e) => setResolutionNote(e.target.value)}
        placeholder="How was this resolved?"
        rows={2}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-xs text-brand-charcoal hover:bg-brand-white">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-lg bg-brand-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
        >
          {isSubmitting ? <Loader2Icon className="inline size-3.5 animate-spin" /> : 'Confirm resolution'}
        </button>
      </div>
    </div>
  );
}

// ── Pending finding card ────────────────────────────────────────────────
function PendingFindingCard({ finding, onResolved }: { finding: PendingFinding; onResolved: () => void }) {
  return (
    <div className="rounded-xl border border-brand-border bg-brand-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-brand-black">{finding.outcomeName}</span>
            {severityBadge(finding.severity)}
          </div>
          <p className="mt-1 text-sm text-brand-charcoal">{finding.reason}</p>
          <p className="mt-1 text-xs font-mono text-brand-gray">
            {finding.transactionId} · {formatDate(finding.createdAt)}
          </p>
          <ResolveFindingInline finding={finding} onResolved={onResolved} />
        </div>
      </div>
    </div>
  );
}

export const DashboardDetectionFindings: React.FC = () => {
  const [data, setData] = useState<FindingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch('/detection/findings');
      if (!res.ok) throw new Error('Failed to load detection findings.');
      const body: FindingsResponse = await res.json();
      setData(body);
    } catch (err: any) {
      setError(err.message || 'Could not load detection findings.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-brand-black">Detection</h1>
        <p className="text-sm text-brand-gray mt-1">
          What your detection outcomes have found — and what still needs your review.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.runs.length === 0 && (
        <div className="rounded-xl border border-dashed border-brand-border p-6 text-center">
          <p className="text-sm text-brand-gray">
            No detection runs yet — nothing to show until an outcome runs its first check.
          </p>
        </div>
      )}

      {/* Scenario 2: something needs judgment — shown first, most important */}
      {data && data.totalPending > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-brand-black">
              Needs your review ({data.totalPending})
            </h3>
            <a
              href="/dashboard/chat-home"
              className="inline-flex items-center gap-1.5 text-xs text-brand-accent hover:underline"
            >
              <MessageSquareIcon className="size-3.5" /> Or resolve via chat
            </a>
          </div>
          {data.pending.map((f) => (
            <PendingFindingCard key={`${f.runId}-${f.transactionId}`} finding={f} onResolved={load} />
          ))}
          {data.truncated && (
            <p className="text-xs text-brand-gray italic">
              Showing the first page of results for at least one run — open that run's full detail via chat for the rest.
            </p>
          )}
        </div>
      )}

      {/* Scenario 1: nothing needs me right now — still show recent activity so
          the merchant knows detection is active, not silent. */}
      {data && data.totalPending === 0 && data.runs.length > 0 && (
        <div className="rounded-xl border border-brand-border bg-green-50/40 p-4 text-center">
          <CheckCircle2Icon className="mx-auto size-5 text-green-600 mb-1" />
          <p className="text-sm text-brand-charcoal">Nothing pending — all clear.</p>
        </div>
      )}

      {/* Recent runs — Scenario 3, always visible regardless of pending state */}
      {data && data.runs.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-brand-black">Recent runs</h3>
          {data.runs.slice(0, 10).map((run) => (
            <RunSummaryRow key={run.runId} run={run} />
          ))}
        </div>
      )}
    </div>
  );
};